import React, { useState, useEffect } from 'react';
import Icon from '../AppIcon';
import alertService from '../../services/alertService';

const RealtimeNotificationPanel = ({ onAlertClick }) => {
  const [alerts, setAlerts] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    loadAlerts();
    
    // Subscribe to real-time alerts
    const channel = alertService?.subscribeToAlerts((newAlert) => {
      setAlerts(prev => [newAlert, ...prev]);
      if (newAlert?.alertStatus === 'unread') {
        setUnreadCount(prev => prev + 1);
      }
    });
    
    return () => {
      alertService?.unsubscribeFromAlerts(channel);
    };
  }, []);

  const loadAlerts = async () => {
    try {
      setLoading(true);
      const data = await alertService?.getAllAlerts();
      setAlerts(data);
      const unread = data?.filter(a => a?.alertStatus === 'unread')?.length || 0;
      setUnreadCount(unread);
    } catch (err) {
      setError(err?.message);
    } finally {
      setLoading(false);
    }
  };

  const handleMarkAsRead = async (alertId) => {
    try {
      await alertService?.markAsRead(alertId);
      setAlerts(prev => prev?.map(alert => 
        alert?.id === alertId ? { ...alert, alertStatus: 'read' } : alert
      ));
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (err) {
      console.error('Failed to mark as read:', err);
    }
  };

  const handleDismiss = async (alertId) => {
    try {
      await alertService?.dismissAlert(alertId);
      setAlerts(prev => prev?.filter(alert => alert?.id !== alertId));
      const alert = alerts?.find(a => a?.id === alertId);
      if (alert?.alertStatus === 'unread') {
        setUnreadCount(prev => Math.max(0, prev - 1));
      }
    } catch (err) {
      console.error('Failed to dismiss alert:', err);
    }
  };

  const handleMarkAllAsRead = async () => {
    try {
      await alertService?.markAllAsRead();
      setAlerts(prev => prev?.map(alert => ({ ...alert, alertStatus: 'read' })));
      setUnreadCount(0);
    } catch (err) {
      console.error('Failed to mark all as read:', err);
    }
  };

  const getSeverityStyles = (severity) => {
    switch (severity) {
      case 'critical':
        return {
          bg: 'bg-error/10',
          text: 'text-error',
          icon: 'AlertTriangle',
          badge: 'bg-error text-error-foreground'
        };
      case 'high':
        return {
          bg: 'bg-warning/10',
          text: 'text-warning',
          icon: 'AlertCircle',
          badge: 'bg-warning text-warning-foreground'
        };
      case 'medium':
        return {
          bg: 'bg-accent/10',
          text: 'text-accent',
          icon: 'Info',
          badge: 'bg-accent text-accent-foreground'
        };
      default:
        return {
          bg: 'bg-muted',
          text: 'text-muted-foreground',
          icon: 'Info',
          badge: 'bg-muted text-muted-foreground'
        };
    }
  };

  const formatTimestamp = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date?.toLocaleDateString('en-GB', { day: '2-digit', month: 'short' });
  };

  return (
    <div className="relative">
      {/* Notification Bell Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="relative flex items-center justify-center w-10 h-10 rounded-md hover:bg-muted transition-smooth"
        aria-label="Notifications"
      >
        <Icon name="Bell" size={20} color="var(--color-foreground)" />
        {unreadCount > 0 && (
          <span className="absolute top-1 right-1 flex items-center justify-center w-5 h-5 text-xs font-bold bg-error text-error-foreground rounded-full">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>
      {/* Notification Dropdown Panel */}
      {isOpen && (
        <div className="absolute right-0 top-12 w-96 max-w-[calc(100vw-2rem)] bg-card border border-border rounded-lg shadow-lg z-50">
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-border">
            <div className="flex items-center gap-2">
              <Icon name="Bell" size={18} color="var(--color-primary)" />
              <h3 className="font-heading font-semibold text-base text-foreground">
                Notifications
              </h3>
              {unreadCount > 0 && (
                <span className="px-2 py-0.5 text-xs font-caption bg-error/10 text-error rounded-full">
                  {unreadCount} new
                </span>
              )}
            </div>
            <button
              onClick={() => setIsOpen(false)}
              className="flex items-center justify-center w-8 h-8 rounded-md hover:bg-muted transition-smooth"
              aria-label="Close notifications"
            >
              <Icon name="X" size={16} color="var(--color-muted-foreground)" />
            </button>
          </div>

          {/* Actions */}
          {unreadCount > 0 && (
            <div className="px-4 py-2 border-b border-border">
              <button
                onClick={handleMarkAllAsRead}
                className="text-xs text-primary hover:underline"
              >
                Mark all as read
              </button>
            </div>
          )}

          {/* Alert List */}
          <div className="max-h-96 overflow-y-auto">
            {loading ? (
              <div className="flex items-center justify-center py-8">
                <Icon name="Loader" size={24} color="var(--color-muted-foreground)" className="animate-spin" />
              </div>
            ) : error ? (
              <div className="p-4 text-center text-sm text-blue-700">
                {error}
              </div>
            ) : alerts?.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-8 px-4">
                <Icon name="CheckCircle" size={48} color="var(--color-success)" className="mb-3" />
                <p className="text-sm text-muted-foreground">No notifications</p>
              </div>
            ) : (
              alerts?.map((alert) => {
                const styles = getSeverityStyles(alert?.severity);
                return (
                  <div
                    key={alert?.id}
                    className={`p-4 border-b border-border hover:bg-muted/50 transition-smooth cursor-pointer ${
                      alert?.alertStatus === 'unread' ? 'bg-accent/5' : ''
                    }`}
                    onClick={() => {
                      if (alert?.alertStatus === 'unread') {
                        handleMarkAsRead(alert?.id);
                      }
                      onAlertClick?.(alert);
                    }}
                  >
                    <div className="flex items-start gap-3">
                      <div className={styles?.text}>
                        <Icon name={styles?.icon} size={18} color="currentColor" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-2 mb-1">
                          <h4 className="font-body font-semibold text-sm text-foreground line-clamp-1">
                            {alert?.title}
                          </h4>
                          {alert?.alertStatus === 'unread' && (
                            <span className="w-2 h-2 bg-primary rounded-full flex-shrink-0 mt-1"></span>
                          )}
                        </div>
                        <p className="text-xs text-muted-foreground mb-2 line-clamp-2">
                          {alert?.message}
                        </p>
                        <div className="flex items-center justify-between">
                          <span className="text-xs caption text-muted-foreground">
                            {formatTimestamp(alert?.createdAt)}
                          </span>
                          <button
                            onClick={(e) => {
                              e?.stopPropagation();
                              handleDismiss(alert?.id);
                            }}
                            className="text-xs text-muted-foreground hover:text-foreground transition-smooth"
                          >
                            Dismiss
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}
      {/* Overlay */}
      {isOpen && (
        <div
          className="fixed inset-0 z-40"
          onClick={() => setIsOpen(false)}
        ></div>
      )}
    </div>
  );
};

export default RealtimeNotificationPanel;